import React from 'react';

const StructuredData = ({ type = 'organization', data = {} }) => {
  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Digital Corp France",
    "legalName": "Digital Corp France",
    "url": "https://digitalcorpfrance.com",
    "logo": "https://digitalcorpfrance.com/logo.png",
    "foundingDate": "2024",
    "description": "Professional AI-powered website and SaaS application development. Custom business automation, web & mobile development, digital transformation services.",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "FR",
      "addressRegion": "France"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "email": "digitalcorpfrance@gmail.com",
      "availableLanguage": ["English", "French", "Spanish", "German", "Italian", "Portuguese", "Russian", "Chinese", "Arabic", "Hindi", "Japanese"]
    },
    "sameAs": [
      "https://digitalcorpfrance.com"
    ],
    "knowsAbout": [
      "Web Development",
      "Mobile App Development",
      "SaaS Application Development",
      "Artificial Intelligence",
      "Business Automation",
      "Digital Transformation",
      "Custom Software Development"
    ],
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "48.8566",
        "longitude": "2.3522"
      },
      "geoRadius": "Global"
    },
    "makesOffer": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "AI Website Development",
          "description": "Custom AI-powered website development with modern technologies"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "SaaS Application Development",
          "description": "End-to-end SaaS application creation and deployment"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Mobile App Development",
          "description": "Native iOS and Android mobile application development"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Business Automation",
          "description": "Custom business process automation with AI integration"
        }
      }
    ]
  };

  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Digital Corp France",
    "image": "https://digitalcorpfrance.com/logo.png",
    "telephone": "",
    "email": "digitalcorpfrance@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "48.8566",
      "longitude": "2.3522"
    },
    "url": "https://digitalcorpfrance.com",
    "priceRange": "€€€",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "09:00",
      "closes": "18:00"
    }
  };

  // WebSite Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Digital Corp France",
    "url": "https://digitalcorpfrance.com",
    "description": "Professional AI-powered website and SaaS application development services",
    "publisher": {
      "@type": "Organization",
      "name": "Digital Corp France"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://digitalcorpfrance.com/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    "inLanguage": ["en", "fr", "es", "de", "it", "pt", "ru", "zh", "ar", "hi", "ja"]
  };

  // Article Schema (for blog posts)
  const articleSchema = data.article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": data.article.title || "Digital Corp France Article",
    "description": data.article.excerpt || "",
    "image": data.article.image || "https://digitalcorpfrance.com/blog-default.jpg",
    "author": {
      "@type": "Organization",
      "name": "Digital Corp France"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Digital Corp France",
      "logo": {
        "@type": "ImageObject",
        "url": "https://digitalcorpfrance.com/logo.png"
      }
    },
    "datePublished": data.article.publishedAt || new Date().toISOString(),
    "dateModified": data.article.modifiedAt || new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": data.article.url || "https://digitalcorpfrance.com"
    }
  } : null;

  // BreadcrumbList Schema
  const breadcrumbSchema = data.breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": data.breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  } : null;

  // Service Schema
  const serviceSchema = data.service ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": data.service.name,
    "description": data.service.description,
    "url": data.service.url,
    "serviceType": data.service.name,
    "provider": {
      "@type": "Organization",
      "name": "Digital Corp France",
      "url": "https://digitalcorpfrance.com"
    },
    "areaServed": "Global"
  } : null;

  // FAQPage Schema
  const faqSchema = (data.faq && data.faq.length) ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.faq.map((item) => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  } : null;

  // Select schema based on type
  let schema;
  switch(type) {
    case 'organization':
      schema = organizationSchema;
      break;
    case 'localbusiness':
      schema = localBusinessSchema;
      break;
    case 'website':
      schema = websiteSchema;
      break;
    case 'article':
      schema = articleSchema;
      break;
    case 'breadcrumb':
      schema = breadcrumbSchema;
      break;
    case 'service':
      schema = serviceSchema;
      break;
    case 'faqpage':
      schema = faqSchema;
      break;
    default:
      schema = organizationSchema;
  }

  if (!schema) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema).replace(/</g, '\\u003c') }}
    />
  );
};

export default StructuredData;
